import React from "react";
import { XCircle, AlertTriangle, Lock } from "lucide-react";
import { StatusMessages } from "../statusMessages";
import { APIError } from "src/errors";
import { Modal, ModalProps, HomeButton } from "..";

interface ModalWithPresetIconProps extends Omit<ModalProps, "icon"> {}
interface ModalWithPresetChildrenProps extends Omit<ModalWithPresetIconProps, "children"> {}

interface SuccessModalProps extends ModalWithPresetIconProps {}

interface WarnModalProps extends ModalWithPresetIconProps, ModalWithPresetChildrenProps {
  onCancel: () => void;
  onConfirm: () => void;
}

interface ActionRequiredProps extends ModalWithPresetIconProps {}

interface ErrorModalProps extends ModalWithPresetIconProps {}

interface ForbiddenModalProps extends ModalWithPresetIconProps {}

interface AuthorisationErrorModalProps extends ModalWithPresetChildrenProps {
  onRetry: () => void;
}

interface DataFetchErrorModalProps extends ErrorModalProps {
  onRetry: () => void;
}

interface FileLoadErrorModalProps extends ErrorModalProps, ModalWithPresetChildrenProps {
  onRetry: () => void;
  // When the failure is a backend validation error (4xx), surface its message instead of the
  // generic "check your internet connection" copy.
  error?: unknown;
}

interface SubmissionErrorModalProps extends ErrorModalProps, ModalWithPresetChildrenProps {
  onRetry: () => void;
  showHomeButton?: boolean;
}

interface UpdateErrorModalProps extends ErrorModalProps, ModalWithPresetChildrenProps {
  onRetry: () => void;
  showHomeButton?: boolean;
}

const SuccessModal: React.FC<SuccessModalProps> = ({
  header = "Success",
  message = <StatusMessages.Success />,
  children = <HomeButton />,
  onClose
}) => {
  return <Modal header={header} message={message} children={children} onClose={onClose} />;
};

const WarnModal: React.FC<WarnModalProps> = ({
  header = "This Action Cannot be Undone",
  message = <StatusMessages.Warn />,
  onCancel,
  onConfirm,
  onClose
}) => {
  const children = (
    <div className="inline-flex gap-2.5 max-w-[300px]">
      <button
        className="text-black border border-black bg-white text-sm font-normal py-1.5 px-0 transition-all duration-300 rounded-[32px] w-[200px] min-w-[50px] hover:cursor-pointer disabled:cursor-auto disabled:text-gray-300 text-center"
        onClick={onCancel}
      >
        Cancel
      </button>
      <button
        className="text-black border border-black bg-white text-sm font-normal py-1.5 px-0 transition-all duration-300 rounded-[32px] w-[200px] min-w-[50px] hover:cursor-pointer disabled:cursor-auto disabled:text-gray-300 text-center"
        onClick={onConfirm}
      >
        Confirm
      </button>
    </div>
  );

  return <Modal header={header} message={message} children={children} icon={<AlertTriangle />} onClose={onClose} />;
};

const ActionRequired: React.FC<ActionRequiredProps> = ({
  header = "Action Required",
  message = <StatusMessages.ActionRequired />,
  children = <HomeButton />,
  onClose
}) => {
  return <Modal header={header} message={message} children={children} onClose={onClose} />;
};

const ForbiddenModal: React.FC<ForbiddenModalProps> = ({
  header = "Forbidden",
  message = <StatusMessages.Forbidden />,
  children = <HomeButton />,
  onClose
}) => {
  return <Modal header={header} message={message} children={children} icon={<Lock />} onClose={onClose} />;
};

const ErrorModal: React.FC<ErrorModalProps> = ({
  header = "An Error Occurred",
  message = <StatusMessages.Error />,
  children = <HomeButton />,
  onClose
}) => {
  return <Modal header={header} message={message} children={children} icon={<XCircle />} onClose={onClose} />;
};

const AuthorisationErrorModal: React.FC<AuthorisationErrorModalProps> = ({
  header = "Unauthorised",
  message = <StatusMessages.AuthorisationError />,
  onRetry,
  onClose
}) => {
  const children = (
    <button
      className="text-black border border-black bg-white text-sm font-normal py-1.5 px-0 transition-all duration-300 rounded-[32px] w-[200px] min-w-[50px] hover:cursor-pointer disabled:cursor-auto disabled:text-gray-300 text-center"
      onClick={onRetry}
    >
      Try Again
    </button>
  );

  return <ErrorModal header={header} message={message} children={children} onClose={onClose} />;
};

const FileLoadErrorModal: React.FC<FileLoadErrorModalProps> = ({
  header = "File Load Error",
  message,
  error,
  onRetry,
  onClose
}) => {
  const isValidationError = error instanceof APIError && error.type === "CLIENT_ERROR" && !!error.message;
  const displayMessage = message ?? (isValidationError ? error.message : <StatusMessages.FileLoadError />);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <XCircle className="w-6 h-6 text-error-600" />
            <h2 className="text-lg font-semibold text-gray-900">{header}</h2>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
              &times;
            </button>
          )}
        </div>

        <div className="p-5 space-y-4">
          <div className="text-sm text-gray-700">{displayMessage}</div>

          {/* Footer Buttons */}
          <div className="flex justify-center space-x-3 pt-2">
            {onClose && (
              <button
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors text-sm font-medium"
                onClick={onClose}
              >
                Close
              </button>
            )}
            <button
              className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm font-medium"
              onClick={onRetry}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const DataFetchErrorModal: React.FC<DataFetchErrorModalProps> = ({
  header = "Data Fetch Error",
  message = <StatusMessages.DataFetchError />,
  onRetry,
  onClose
}) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <XCircle className="w-6 h-6 text-error-600" />
            <h2 className="text-lg font-semibold text-gray-900">{header}</h2>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
              &times;
            </button>
          )}
        </div>

        <div className="p-5 space-y-4">
          <div className="text-sm text-gray-700">{message}</div>

          {/* Footer Button */}
          <div className="flex justify-center pt-2">
            <button
              className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm font-medium"
              onClick={onRetry}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const SubmissionErrorModal: React.FC<SubmissionErrorModalProps> = ({
  header = "An Error Occurred",
  message = <StatusMessages.SubmissionError />,
  onRetry,
  onClose,
  showHomeButton = false
}) => {
  const children = (
    <div className="inline-flex gap-2.5 max-w-[300px]">
      {showHomeButton && <HomeButton />}
      <button
        className="text-black border border-black bg-white text-sm font-normal py-1.5 px-0 transition-all duration-300 rounded-[32px] w-[200px] min-w-[50px] hover:cursor-pointer disabled:cursor-auto disabled:text-gray-300 text-center"
        onClick={onRetry}
      >
        Try Again
      </button>
    </div>
  );

  return <ErrorModal header={header} message={message} children={children} onClose={onClose} />;
};

const UpdateErrorModal: React.FC<UpdateErrorModalProps> = ({
  header = "An Error Occurred",
  message = <StatusMessages.UpdateError />,
  onRetry,
  onClose,
  showHomeButton = false
}) => {
  const children = (
    <div className="inline-flex gap-2.5 max-w-[300px]">
      {showHomeButton && <HomeButton />}
      <button
        className="text-black border border-black bg-white text-sm font-normal py-1.5 px-0 transition-all duration-300 rounded-[32px] w-[200px] min-w-[50px] hover:cursor-pointer disabled:cursor-auto disabled:text-gray-300 text-center"
        onClick={onRetry}
      >
        Try Again
      </button>
    </div>
  );

  return <ErrorModal header={header} message={message} children={children} onClose={onClose} />;
};

export const StatusModals = {
  Success: SuccessModal,
  ActionRequired: ActionRequired,
  Warn: WarnModal,
  Forbidden: ForbiddenModal,
  Error: ErrorModal,
  AuthorisationError: AuthorisationErrorModal,
  FileLoadError: FileLoadErrorModal,
  DataFetchError: DataFetchErrorModal,
  SubmissionError: SubmissionErrorModal,
  UpdateError: UpdateErrorModal
};
